/**
 * JaimeNunezTraining - Entrenamiento "101 en 6 dardos empezando en doble"
 * 501 Dart League Web
 *
 * 9 niveles — los dobles más usados en competición: D20, D18, D16, D12, D10, D8, D4, D2 + Bull.
 * Fase 1 - Activación: 1 intento para acertar el doble. Si fallas, se reinicia el nivel.
 * Fase 2 - Cierre: 1 tirada completa (3 dardos) para cerrar el score restante.
 * Guarda estadísticas de intentos por doble para el resumen final.
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { getCheckoutOptions, formatCheckout } from './utils/checkouts';
import SponsorsStrip from './SponsorsStrip';
import './styles/index.css';
import './styles/jaime-nunez.css';

// Galería de imágenes para la intro
const GALLERY_IMAGES = [
  {
    id: 1,
    src: '/images/JaimeNunez/JaimeN.jpg',
    title: 'Anillo de La Carrera 2024',
    description: 'Uno de los últimos grandes hitos de Jaime Núñez',
    fit: 'cover'
  },
  {
    id: 2,
    src: '/images/JaimeNunez/MediterraneanOpen.jpg',
    title: 'Mediterranean Open 2023',
    description: 'Campeón en categoría general — Torneo donde Luke Littler ganó en juvenil',
    fit: 'contain'
  },
  {
    id: 3,
    src: '/images/JaimeNunez/Elite.jpg',
    title: 'Nº1 Categoría Elite',
    description: 'Radikal Players — Máxima clasificación del dardo electrónico en España',
    fit: 'cover'
  },
  {
    id: 4,
    src: '/images/JaimeNunez/jaime501dart.png',
    title: 'Jaime en 501 Dart League',
    description: 'Nos ilusiona ver a Jaime competir en 501 Dart League e integrar la app en su preparación habitual.',
    fit: 'cover'
  },
];

// Niveles: los 9 dobles más usados en competición (D20 → D2 + Bull)
const LEVELS = [
  { double: 20, value: 40, label: 'D20' },
  { double: 18, value: 36, label: 'D18' },
  { double: 16, value: 32, label: 'D16' },
  { double: 12, value: 24, label: 'D12' },
  { double: 10, value: 20, label: 'D10' },
  { double: 8,  value: 16, label: 'D8'  },
  { double: 4,  value: 8,  label: 'D4'  },
  { double: 2,  value: 4,  label: 'D2'  },
  { double: 'Bull', value: 50, label: 'Bull' },
];

const TOTAL_LEVELS = LEVELS.length;
const STARTING_SCORE = 101;

// Standard dartboard number order (clockwise from top)
const BOARD_ORDER = [20, 1, 18, 4, 13, 6, 10, 15, 2, 17, 3, 19, 7, 16, 8, 11, 14, 9, 12, 5];
const SEG_ANGLE = 18; // 360 / 20

// Convert polar (degrees from 12 o'clock, clockwise) to cartesian
const pol = (r, deg) => {
  const rad = (deg - 90) * Math.PI / 180;
  return { x: r * Math.cos(rad), y: r * Math.sin(rad) };
};

const arcPath = (rOut, rIn, s, e) => {
  const a = pol(rOut, s), b = pol(rOut, e), c = pol(rIn, e), d = pol(rIn, s);
  const lg = e - s > 180 ? 1 : 0;
  return `M${a.x},${a.y}A${rOut},${rOut} 0 ${lg} 1 ${b.x},${b.y}L${c.x},${c.y}A${rIn},${rIn} 0 ${lg} 0 ${d.x},${d.y}Z`;
};

const DartboardProgress = ({ levels, currentLevel: curLvl }) => {
  // Map double number → level index
  const dblIdx = {};
  levels.forEach((l, i) => { if (l.double !== 'Bull') dblIdx[l.double] = i; });
  const bullIdx = levels.findIndex(l => l.double === 'Bull');

  const segColor = (idx) => {
    if (idx < curLvl) return { fill: 'rgba(0,200,83,0.3)', stroke: 'rgba(0,200,83,0.6)' };
    if (idx === curLvl) return { fill: 'rgba(0,198,207,0.35)', stroke: 'rgba(0,198,207,0.8)' };
    return { fill: 'rgba(255,255,255,0.04)', stroke: 'rgba(255,255,255,0.08)' };
  };

  return (
    <svg viewBox="-175 -175 350 350" className="jn-dartboard-svg">
      {/* Board background */}
      <circle cx="0" cy="0" r="150" fill="rgba(0,0,0,0.3)" stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" />
      {/* Inner decorative rings */}
      <circle cx="0" cy="0" r="118" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="0.5" />
      <circle cx="0" cy="0" r="85" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="0.5" />
      <circle cx="0" cy="0" r="52" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="0.5" />

      {/* Double ring segments */}
      {BOARD_ORDER.map((num, i) => {
        const s = i * SEG_ANGLE - 9, e = s + SEG_ANGLE;
        const idx = dblIdx[num];
        const c = segColor(idx);
        return (
          <path key={num} d={arcPath(148, 126, s, e)}
            fill={c.fill} stroke={c.stroke} strokeWidth="0.5"
            className={idx === curLvl ? 'jn-seg-active' : ''} />
        );
      })}

      {/* Segment dividers + numbers */}
      {BOARD_ORDER.map((num, i) => {
        const ang = i * SEG_ANGLE - 9;
        const p1 = pol(126, ang), p2 = pol(148, ang);
        const tp = pol(158, i * SEG_ANGLE);
        const idx = dblIdx[num];
        const done = idx < curLvl, active = idx === curLvl;
        const col = done ? '#00e053' : active ? '#00C6CF' : '#505860';
        return (
          <g key={`n${num}`}>
            <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="rgba(255,255,255,0.06)" strokeWidth="0.5" />
            <text x={tp.x} y={tp.y} textAnchor="middle" dominantBaseline="central"
              fill={col} fontSize={active ? '13' : '10'} fontWeight={active ? '800' : '600'}
              style={{ fontFamily: 'system-ui, sans-serif' }}>
              {num}
            </text>
          </g>
        );
      })}

      {/* Bull */}
      {(() => {
        const bc = segColor(bullIdx);
        return (
          <>
            <circle cx="0" cy="0" r="22" fill={bc.fill} stroke={bc.stroke} strokeWidth="0.5" />
            <circle cx="0" cy="0" r="9" fill={bullIdx < curLvl ? 'rgba(0,200,83,0.4)' : bullIdx === curLvl ? 'rgba(0,198,207,0.5)' : 'rgba(255,255,255,0.06)'}
              stroke={bc.stroke} strokeWidth="0.5"
              className={bullIdx === curLvl ? 'jn-seg-active' : ''} />
            <text x="0" y="0" textAnchor="middle" dominantBaseline="central"
              fill={bullIdx < curLvl ? '#00e053' : bullIdx === curLvl ? '#00C6CF' : '#505860'}
              fontSize="7" fontWeight="700" style={{ fontFamily: 'system-ui, sans-serif' }}>
              BULL
            </text>
          </>
        );
      })()}
    </svg>
  );
};

const JaimeNunezTraining = ({ onBack }) => {
  // --- Game state ---
  const [gamePhase, setGamePhase] = useState('intro');
  const [currentLevel, setCurrentLevel] = useState(0);
  const [doubleActivated, setDoubleActivated] = useState(false);
  const [activationStep, setActivationStep] = useState(1);
  const [score, setScore] = useState(STARTING_SCORE);
  const [input, setInput] = useState('');

  // --- Stats (per-level tracking) ---
  const [levelStats, setLevelStats] = useState([]);          // { attempts } per completed level
  const [currentLevelAttempts, setCurrentLevelAttempts] = useState(0);
  const [totalFailures, setTotalFailures] = useState(0);     // running total for display
  const [visitHistory, setVisitHistory] = useState([]);       // current level only

  // --- UI ---
  const [alert, setAlert] = useState({ visible: false, title: '', message: '', type: '' });

  // --- Gallery ---
  const [activeSlide, setActiveSlide] = useState(0);
  const trainingRootRef = useRef(null);
  const carouselRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const scrollToSlide = useCallback((index) => {
    if (carouselRef.current) {
      const slides = carouselRef.current.querySelectorAll('.jn-gallery-slide');
      const targetSlide = slides[index];
      if (!targetSlide) return;
      const left = targetSlide.offsetLeft - ((carouselRef.current.clientWidth - targetSlide.clientWidth) / 2);
      carouselRef.current.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
      setActiveSlide(index);
    }
  }, []);
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && activeSlide < GALLERY_IMAGES.length - 1) scrollToSlide(activeSlide + 1);
      else if (diff < 0 && activeSlide > 0) scrollToSlide(activeSlide - 1);
    }
  };

  // Auto-scroll galería solo en intro
  useEffect(() => {
    if (gamePhase !== 'intro') return undefined;

    const timer = window.setTimeout(() => {
      const next = (activeSlide + 1) % GALLERY_IMAGES.length;
      scrollToSlide(next);
    }, 4000);

    return () => window.clearTimeout(timer);
  }, [activeSlide, gamePhase, scrollToSlide]);

  const level = LEVELS[currentLevel] || LEVELS[0];

  const checkoutOptions = useMemo(() => {
    if (!doubleActivated || score > 170 || score < 2) return [];
    return getCheckoutOptions(score);
  }, [score, doubleActivated]);

  // --- Helpers ---
  const showAlert = useCallback((title, message, type = 'info') => {
    setAlert({ visible: true, title, message, type });
  }, []);

  const closeAlert = useCallback(() => {
    setAlert({ visible: false, title: '', message: '', type: '' });
  }, []);

  const resetLevel = useCallback(() => {
    setDoubleActivated(false);
    setActivationStep(1);
    setScore(STARTING_SCORE);
    setVisitHistory([]);
    setInput('');
  }, []);

  const advanceLevel = useCallback(() => {
    const attempts = currentLevelAttempts + 1;
    setLevelStats(prev => [...prev, { attempts }]);
    const nextLevel = currentLevel + 1;
    if (nextLevel >= TOTAL_LEVELS) {
      setGamePhase('results');
    } else {
      showAlert('¡Nivel superado!', `${level.label} completado${attempts > 1 ? ` (${attempts} intentos)` : ''}. Siguiente: ${LEVELS[nextLevel].label}`, 'success');
      setCurrentLevel(nextLevel);
      setCurrentLevelAttempts(0);
      resetLevel();
    }
  }, [currentLevel, currentLevelAttempts, level, resetLevel, showAlert]);

  const failLevel = useCallback((title, message) => {
    setCurrentLevelAttempts(prev => prev + 1);
    setTotalFailures(prev => prev + 1);
    showAlert(title, message, 'error');
    resetLevel();
  }, [resetLevel, showAlert]);

  // --- Actions ---
  const handleStart = useCallback(() => {
    setGamePhase('playing');
    setCurrentLevel(0);
    setLevelStats([]);
    setCurrentLevelAttempts(0);
    setTotalFailures(0);
    resetLevel();
  }, [resetLevel]);

  const handleDoubleActivated = useCallback((extraPoints) => {
    const newScore = score - extraPoints;
    if (newScore === 1) {
      failLevel('¡Bust!', 'No puedes quedarte en 1 punto.');
      return;
    }
    setDoubleActivated(true);
    setScore(newScore);
    setVisitHistory(prev => [...prev, {
      type: 'activation', extra: extraPoints, points: level.value + extraPoints
    }]);
    if (newScore === 0) { advanceLevel(); return; }
    if (newScore < 0) {
      failLevel('¡Bust!', 'Has pasado de 0. Se reinicia el nivel.');
      return;
    }
    setInput('');
  }, [score, level, advanceLevel, failLevel]);

  const handleDoubleMissed = useCallback(() => {
    setVisitHistory(prev => [...prev, { type: 'miss' }]);
    failLevel('Nivel fallido', `No activaste ${level.label}. Intento fallido.`);
  }, [level, failLevel]);

  const handleNumberPress = useCallback((num) => {
    setInput(prev => {
      const next = prev + num;
      return parseInt(next, 10) > 180 ? prev : next;
    });
  }, []);

  const handleBackspace = useCallback(() => setInput(prev => prev.slice(0, -1)), []);

  const handleBust = useCallback(() => {
    setVisitHistory(prev => [...prev, { type: 'bust' }]);
    failLevel('¡Bust!', 'Se reinicia el nivel.');
  }, [failLevel]);

  const confirmBustAlert = useCallback(() => {
    setAlert({ visible: false, title: '', message: '', type: '' });
    setInput('');
    handleBust();
  }, [handleBust]);

  const handleScoringSubmit = useCallback(() => {
    const pts = parseInt(input, 10) || 0;
    if (pts > 180) return;
    const newScore = score - pts;
    if (newScore < 0) { showAlert('Inválido', `Máximo: ${score}`, 'error'); return; }
    if (newScore === 1) { showAlert('Inválido', 'No puedes quedarte en 1 punto.', 'error'); return; }
    if (newScore === 0) {
      setVisitHistory(prev => [...prev, { type: 'checkout', points: pts }]);
      setScore(0);
      advanceLevel();
      return;
    }
    setScore(newScore);
    setVisitHistory(prev => [...prev, { type: 'score', points: pts }]);
    setInput('');
    // Solo tienes 1 tirada de cierre — si no cierras, fallo
    failLevel('No cerrado', `Te quedaron ${newScore} puntos.`);
  }, [input, score, advanceLevel, failLevel, showAlert]);

  const handleDoubleHit = useCallback(() => {
    setScore(STARTING_SCORE - level.value);
    setActivationStep(2);
  }, [level]);

  const handleExtraPointsSubmit = useCallback(() => {
    const pts = parseInt(input, 10) || 0;
    if (pts > 180) return;
    const remaining = score; // score ya tiene el doble restado
    if (pts > remaining) {
      // Más de lo necesario — confirmar si se pasó (bust)
      setAlert({
        visible: true,
        title: '¿Te has pasado?',
        message: `Has marcado ${pts} puntos extra, pero solo te quedan ${remaining}. ¿Te pasaste?`,
        type: 'warning',
        confirmBust: true,
      });
      return;
    }
    const newScore = remaining - pts;
    if (newScore === 1) {
      setAlert({
        visible: true,
        title: '¿Te has pasado?',
        message: `Te quedarías en 1 punto, lo cual no es posible. ¿Te pasaste?`,
        type: 'warning',
        confirmBust: true,
      });
      return;
    }
    setInput('');
    handleDoubleActivated(pts);
  }, [input, score, handleDoubleActivated]);

  const handleNoExtraPoints = useCallback(() => { setInput(''); handleDoubleActivated(0); }, [handleDoubleActivated]);

  const renderMobileNumpad = (showBust, onSubmit) => {
    const rows = showBust
      ? [
          ['1', '2', '3', input.length > 0 ? '←' : 'BUST'],
          ['4', '5', '6', '0'],
          ['7', '8', '9', 'OK'],
        ]
      : [
          ['1', '2', '3', input.length > 0 ? '←' : ''],
          ['4', '5', '6', '0'],
          ['7', '8', '9', 'OK'],
        ];

    return (
      <div className="jn-mobile-numpad">
        {rows.map((row, rowIndex) => (
          <div key={rowIndex} className="jn-mobile-numpad-row">
            {row.map((keyValue, keyIndex) => {
              if (!keyValue) {
                return <div key={`spacer-${rowIndex}-${keyIndex}`} className="jn-mobile-numpad-spacer" />;
              }

              const isBackspace = keyValue === '←';
              const isBustKey = keyValue === 'BUST';
              const isOkKey = keyValue === 'OK';
              const className = [
                'jn-mobile-numpad-btn',
                (isBackspace || isBustKey) ? 'danger' : '',
                isOkKey ? 'confirm' : '',
                (isOkKey || isBustKey) ? 'compact-label' : '',
              ].filter(Boolean).join(' ');

              return (
                <button
                  key={keyValue}
                  className={className}
                  onClick={() => {
                    if (isBackspace) {
                      handleBackspace();
                    } else if (isBustKey) {
                      handleBust();
                    } else if (isOkKey) {
                      onSubmit();
                    } else {
                      handleNumberPress(keyValue);
                    }
                  }}
                >
                  {keyValue}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  // --- Keyboard ---
  const handleKeyDown = useCallback((e) => {
    if (alert.visible || gamePhase !== 'playing') return;
    if (e.key >= '0' && e.key <= '9') handleNumberPress(e.key);
    else if (e.key === 'Backspace') handleBackspace();
    else if (e.key === 'Enter') {
      if (doubleActivated) handleScoringSubmit();
      else if (activationStep === 2) handleExtraPointsSubmit();
    }
  }, [alert.visible, gamePhase, doubleActivated, activationStep, handleNumberPress, handleBackspace, handleScoringSubmit, handleExtraPointsSubmit]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (gamePhase !== 'playing') return undefined;

    const frameId = window.requestAnimationFrame(() => {
      trainingRootRef.current?.scrollIntoView({ block: 'start', behavior: 'auto' });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [gamePhase]);

  // ===========================================================================
  // RENDER
  // ===========================================================================

  // --- INTRO ---
  if (gamePhase === 'intro') {
    return (
      <div className="jn-training">
        <div className="jn-intro">
          <button className="jn-back-btn" onClick={onBack}>← Volver</button>
          <div className="jn-intro-header">
            <img src="/images/JaimeNunez/LogoJaime.png" alt="Jaime Núñez" className="jn-intro-icon" />
            <h1>101 en 6 Dardos</h1>
            <p className="jn-intro-badge">🇪🇸 Jaime Núñez • Málaga, España</p>
          </div>

          {/* Galería de fotos */}
          <div className="jn-gallery">
            <div 
              className="jn-gallery-track" 
              ref={carouselRef}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              {GALLERY_IMAGES.map((img, index) => (
                <div key={img.id} className={`jn-gallery-slide ${activeSlide === index ? 'active' : ''}`}>
                  <div className={`jn-gallery-img-wrapper ${img.fit === 'contain' ? 'contain' : ''}`}>
                    <img src={img.src} alt={img.title} className={`jn-gallery-img ${img.fit === 'contain' ? 'contain' : ''}`} />
                  </div>
                  <h4 className="jn-gallery-title">{img.title}</h4>
                  <p className="jn-gallery-desc">{img.description}</p>
                </div>
              ))}
            </div>
            <div className="jn-gallery-dots">
              {GALLERY_IMAGES.map((_, index) => (
                <button
                  key={index}
                  className={`jn-gallery-dot ${activeSlide === index ? 'active' : ''}`}
                  onClick={() => scrollToSlide(index)}
                  aria-label={`Imagen ${index + 1}`}
                />
              ))}
            </div>
            <div className="jn-gallery-arrows">
              <button 
                className="jn-gallery-arrow" 
                onClick={() => scrollToSlide(Math.max(0, activeSlide - 1))}
                disabled={activeSlide === 0}
              >‹</button>
              <button 
                className="jn-gallery-arrow" 
                onClick={() => scrollToSlide(Math.min(GALLERY_IMAGES.length - 1, activeSlide + 1))}
                disabled={activeSlide === GALLERY_IMAGES.length - 1}
              >›</button>
            </div>
          </div>

          <div className="jn-intro-description">
            <h3>Jaime Núñez</h3>
            <p>Jaime Núñez Cortés es un gran referente de los dardos tanto en acero como en plástico. Empezó a jugar liga a los 14 años, etapa en la que consiguió ganar su primer torneo internacional individual en Elite. Campeón del Mediterranean Open 2023, campeón mundial de La Carrera Radikal 2024 y Nº1 de la clasificación Elite de Radikal Players, entre otros muchos títulos, no necesita más presentación. Si te lo encuentras en la línea de tiro, sabes que estás ante la máxima exigencia.</p>
            <h3>¿En qué consiste?</h3>
            <p>Este es el entrenamiento que Jaime utiliza antes de cada encuentro de liga y competición. El objetivo es alcanzar exactamente 0 puntos partiendo de 101, comenzando siempre con un doble obligatorio.</p>
            <h3>Cómo se juega</h3>
            <ul className="jn-intro-rules">
              <li><strong>Tirada de activación (3 dardos):</strong> debes acertar el doble del nivel. Los dardos anteriores al doble no puntúan. Si no aciertas el doble en esa tirada, el nivel se reinicia.</li>
              <li><strong>Activación en 1.er o 2.º dardo:</strong> en cuanto entra el doble, ese doble ya descuenta de los 101 y los dardos restantes de esa misma tirada se usan para seguir bajando puntuación.</li>
              <li><strong>Tirada de cierre (3 dardos):</strong> tras activar el doble tienes una tirada completa más para cerrar los puntos que queden y llegar a 0.</li>
            </ul>
            <h3>Versión simplificada</h3>
            <p>Hemos adaptado el entrenamiento original para hacerlo más ágil y dinámico. En lugar de recorrer los 21 dobles del tablero (D1 a D20 + Bull), hemos seleccionado únicamente los <strong>dobles más usados en competición</strong>: D20, D18, D16, D12, D10, D8, D4, D2 y Bull — los 9 dobles que más veces deciden un partido.</p>
            <p className="jn-intro-highlight">🎯 «Todo es mentalidad y cierres. Todos sabemos bajar, pero el que gana es el que cierra.» — Jaime Núñez</p>
          </div>

          <div className="jn-intro-levels-preview">
            <h3>9 Niveles — Los dobles más usados en competición</h3>
            <div className="jn-levels-grid">
              {LEVELS.map((l, i) => (
                <div key={i} className="jn-level-chip">
                  <span className="jn-level-num">{i + 1}</span>
                  <span className="jn-level-double">{l.label}</span>
                </div>
              ))}
            </div>
          </div>
          <button className="jn-start-btn" onClick={handleStart}>🎯 Empezar entrenamiento</button>
        </div>
      </div>
    );
  }

  // --- RESULTS ---
  if (gamePhase === 'results') {
    const totalAttempts = levelStats.reduce((sum, s) => sum + s.attempts, 0);
    const firstTryCount = levelStats.filter(s => s.attempts === 1).length;
    const successRate = totalAttempts > 0 ? ((TOTAL_LEVELS / totalAttempts) * 100).toFixed(1) : '0';
    const hardest = levelStats.reduce((max, s, i) => s.attempts > (max.a || 0) ? { a: s.attempts, i } : max, { a: 0 });

    return (
      <div className="jn-training">
        <div className="jn-results">
          <div className="jn-results-header">
            <div className="jn-results-logos">
              <img src="/Logo26.png" alt="501 Dart League" className="jn-results-logo" />
              <img src="/images/JaimeNunez/LogoJaime.png" alt="Jaime Núñez" className="jn-results-player-logo" />
            </div>
            <h1>🏆 ¡Completado!</h1>
            <div className="jn-results-desc-box">
              <strong>101 en 6 Dardos</strong>
              <span>Activa el doble, cierra 101 con los dardos restantes. D20 · D18 · D16 · D12 · D10 · D8 · D4 · D2 · Bull.</span>
            </div>
          </div>

          <div className="jn-results-summary">
            <div className="jn-stat-card highlight">
              <span className="jn-stat-value">{firstTryCount}<small>/{TOTAL_LEVELS}</small></span>
              <span className="jn-stat-label">A la primera</span>
            </div>
            <div className="jn-stat-card">
              <span className="jn-stat-value">{totalAttempts}</span>
              <span className="jn-stat-label">Intentos totales</span>
            </div>
            <div className="jn-stat-card">
              <span className="jn-stat-value">{successRate}%</span>
              <span className="jn-stat-label">Eficiencia</span>
            </div>
            <div className="jn-stat-card">
              <span className="jn-stat-value">{hardest.i !== undefined ? LEVELS[hardest.i].label : '-'}</span>
              <span className="jn-stat-label">Más difícil ({hardest.a}×)</span>
            </div>
          </div>

          <div className="jn-results-detail">
            <h3>Detalle por doble</h3>
            <div className="jn-results-grid">
              {LEVELS.map((l, i) => {
                const stat = levelStats[i];
                const a = stat ? stat.attempts : 0;
                const cls = a === 1 ? 'first' : a === 2 ? 'second' : a > 2 ? 'hard' : '';
                return (
                  <div key={i} className={`jn-result-cell ${cls}`}>
                    <span className="jn-result-double">{l.label}</span>
                    <span className="jn-result-attempts">{a === 1 ? '✓' : `×${a}`}</span>
                  </div>
                );
              })}
            </div>
            <div className="jn-results-legend">
              <span className="jn-legend first">● 1ª</span>
              <span className="jn-legend second">● 2 fallos</span>
              <span className="jn-legend hard">● 3+ fallos</span>
            </div>
          </div>

          <div className="jn-results-actions">
            <button className="jn-btn primary" onClick={handleStart}>🔄 Repetir</button>
            <button className="jn-btn secondary" onClick={onBack}>← Volver</button>
          </div>

          <SponsorsStrip />
        </div>
      </div>
    );
  }

  // --- PLAYING ---
  const isActivation = !doubleActivated;
  const isActivationPrompt = isActivation && activationStep === 1;
  const isActivationFollowUp = isActivation && activationStep === 2;
  const challengeStepLabel = isActivationPrompt
    ? 'Activa el doble'
    : isActivationFollowUp
      ? 'Completa la tirada inicial'
      : 'Cierra';
  const phaseBadgeLabel = isActivationPrompt
    ? `Activa ${level.label}`
    : isActivationFollowUp
      ? 'Dardos restantes'
      : 'Cierra (3 dardos)';
  const extraPointsValue = parseInt(input, 10) || 0;
  const hasExtraPoints = extraPointsValue > 0;
  const activationFollowUpTitle = 'Añade los puntos de los dardos restantes';
  const activationFollowUpHint = `${level.label} activado. Si lo activaste con el primer o segundo dardo, suma aquí lo que hiciste con los dardos restantes de esa misma tirada.`;

  return (
    <div className="jn-training" ref={trainingRootRef}>
      {/* Professional Header */}
      <div className="jn-game-header">
        <button className="jn-back-btn" onClick={onBack}>← Salir</button>
        <div className="jn-header-logos">
          <img src="/Logo26.png" alt="501 Dart League" className="jn-app-logo" />
          <img src="/images/JaimeNunez/LogoJaimeSinfondo.png" alt="Jaime Núñez" className="jn-player-logo" />
        </div>
        <div className="jn-challenge-info">
          <span className="jn-challenge-title">Nivel {currentLevel + 1}/{TOTAL_LEVELS} · {level.label}</span>
          <span className="jn-challenge-desc">101 en 6 Dardos — {challengeStepLabel}</span>
          <span className="jn-challenge-meta">
            {totalFailures > 0 && `Fallos: ${totalFailures}`}{currentLevelAttempts > 0 && ` · Error×${currentLevelAttempts}`}
          </span>
        </div>
      </div>

      {/* Mobile level progress strip */}
      <div className="jn-mobile-levels">
        {LEVELS.map((l, i) => {
          const done = i < currentLevel;
          const active = i === currentLevel;
          return (
            <div key={i} className={`jn-mlevel ${done ? 'done' : ''} ${active ? 'active' : ''}`}
              title={l.label}>
              {done ? '✓' : l.label}
            </div>
          );
        })}
      </div>

      {/* Desktop layout: dartboard + game + numpad */}
      <div className="jn-desktop-layout">

        {/* Left sidebar - dartboard + description (desktop only) */}
        <div className="jn-sidebar jn-sidebar-left">
          <DartboardProgress levels={LEVELS} currentLevel={currentLevel} />
          <div className="jn-sidebar-desc">
            <h4>101 en 6 Dardos</h4>
            <p>1 intento para activar el doble. Si aciertas, 3 dardos extra para cerrar los 101.</p>
            <span className="jn-sidebar-desc-sub">De D1 a D20 + Bull</span>
          </div>
        </div>

        {/* Center: score + actions */}
        <div className="jn-game-center">

      {/* Progress */}
      <div className="jn-progress-bar">
        <div className="jn-progress-fill" style={{ width: `${(currentLevel / TOTAL_LEVELS) * 100}%` }} />
      </div>

      <div className="jn-mobile-hero">
        <div className="jn-mobile-score-row">
          <div className="jn-mobile-dartboard-panel">
            <DartboardProgress levels={LEVELS} currentLevel={currentLevel} />
          </div>
          <div className="jn-mobile-score-stack">
            <span className="jn-mobile-score-number">{score}</span>
            <span className={`jn-phase-badge jn-mobile-phase-badge ${isActivation ? 'activation' : 'scoring'}`}>
              {phaseBadgeLabel}
            </span>
          </div>
        </div>

        {isActivationFollowUp && (
          <p className="jn-mobile-score-context">{activationFollowUpHint}</p>
        )}

        {doubleActivated && checkoutOptions.length > 0 && (
          <div className="jn-mobile-checkouts-wrap">
            <div className="jn-checkouts-row">
              {checkoutOptions.slice(0, 3).map((opt, i) => (
                <span key={i} className="jn-checkout-tag">{formatCheckout(opt)}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Score */}
      <div className="jn-score-row jn-desktop-score-row">
        <span className="jn-score-number">{score}</span>
        <span className={`jn-phase-badge ${isActivation ? 'activation' : 'scoring'}`}>
          {phaseBadgeLabel}
        </span>
      </div>

      {isActivationFollowUp && (
        <p className="jn-score-context jn-desktop-score-context">{activationFollowUpHint}</p>
      )}

      {/* Checkouts */}
      {doubleActivated && checkoutOptions.length > 0 && (
        <div className="jn-checkouts-row jn-desktop-checkouts-row">
          {checkoutOptions.slice(0, 3).map((opt, i) => (
            <span key={i} className="jn-checkout-tag">{formatCheckout(opt)}</span>
          ))}
        </div>
      )}

      {/* Step 1: ¿Doble? */}
      {isActivationPrompt && (
        <>
        <div className="jn-action-panel jn-mobile-only-panel jn-mobile-action-panel">
          <p className="jn-action-title">¿Acertaste el {level.label}? <span className="jn-hint">({level.value} pts)</span></p>
          <p className="jn-mobile-prompt-subtitle">Activa el doble con un solo dardo para poder jugar el resto de la visita.</p>
          <div className="jn-mobile-prompt-stats-row">
            <div className="jn-mobile-prompt-stat-card accent">
              <span className="jn-mobile-prompt-stat-label">Objetivo</span>
              <span className="jn-mobile-prompt-stat-value">{level.label}</span>
            </div>
            <div className="jn-mobile-prompt-stat-card">
              <span className="jn-mobile-prompt-stat-label">Valor</span>
              <span className="jn-mobile-prompt-stat-value">{level.value}</span>
            </div>
            <div className="jn-mobile-prompt-stat-card">
              <span className="jn-mobile-prompt-stat-label">Intento</span>
              <span className="jn-mobile-prompt-stat-value">1 dardo</span>
            </div>
          </div>
          <div className="jn-action-row jn-mobile-action-buttons-row">
            <button className="jn-btn primary" onClick={handleDoubleHit}>Si</button>
            <button className="jn-btn danger" onClick={() => { setInput(''); handleDoubleMissed(); }}>No</button>
          </div>
        </div>

        <div className="jn-action-panel jn-desktop-step-panel">
          <p className="jn-action-title">¿Acertaste {level.label}? <span className="jn-hint">({level.value} pts)</span></p>
          <div className="jn-action-col">
            <button className="jn-btn primary" onClick={handleDoubleHit}>✅ Sí</button>
            <button className="jn-btn danger" onClick={() => { setInput(''); handleDoubleMissed(); }}>❌ No</button>
          </div>
        </div>
        </>
      )}

      {/* Step 2: Puntos extra (mobile: inline, desktop: uses right sidebar) */}
      {isActivationFollowUp && (
        <div className="jn-action-panel jn-mobile-only-panel jn-mobile-action-panel">
          <div className="jn-input-display">{input || '0'}</div>
          {renderMobileNumpad(false, handleExtraPointsSubmit)}
        </div>
      )}

      {/* Scoring (mobile: inline, desktop: uses right sidebar) */}
      {!isActivation && (
        <div className="jn-action-panel jn-mobile-only-panel jn-mobile-action-panel">
          <div className="jn-input-display">{input || '0'}</div>
          {renderMobileNumpad(true, handleScoringSubmit)}
        </div>
      )}

      {/* History */}
      {visitHistory.length > 0 && (
        <div className="jn-history-section jn-desktop-history-section">
          <span className="jn-history-label">Tiradas anteriores:</span>
          <div className="jn-history-row">
            {visitHistory.map((entry, i) => (
              <span key={i} className={`jn-history-tag ${entry.type}`}>
                {entry.type === 'activation' && `✅ ${level.label}+${entry.extra}`}
                {entry.type === 'miss' && '❌ Fallo'}
                {entry.type === 'score' && `${entry.points}pts`}
                {entry.type === 'bust' && 'BUST'}
                {entry.type === 'checkout' && `✅ ${entry.points}pts`}
              </span>
            ))}
          </div>
        </div>
      )}

        </div>{/* end jn-game-center */}

        {/* Right sidebar - numpad (desktop only) */}
        <div className="jn-sidebar jn-sidebar-right">
          {/* Step 2: Puntos extra - desktop numpad */}
          {isActivationFollowUp && (
            <div className="jn-desktop-numpad-panel">
              <p className="jn-action-title">{activationFollowUpTitle}</p>
              <div className="jn-input-display">{input || '0'}</div>
              <div className="jn-numpad compact">
                {[['1','2','3'],['4','5','6'],['7','8','9'],[input.length > 0 ? '←' : '','0','']].map((row, ri) => (
                  <div key={ri} className="jn-numpad-row">
                    {row.map((num, ci) => {
                      if (num === '') return <div key={ci} className="jn-numpad-spacer" />;
                      const isBack = num === '←';
                      return (
                        <button key={ci} className={`jn-numpad-btn ${isBack ? 'danger' : ''}`}
                          onClick={() => isBack ? handleBackspace() : handleNumberPress(num)}>{num}</button>
                      );
                    })}
                  </div>
                ))}
              </div>
              <div className="jn-action-col">
                <button className="jn-btn primary" onClick={handleExtraPointsSubmit} disabled={!hasExtraPoints}>Confirmar puntos</button>
                <button className="jn-btn secondary" onClick={handleNoExtraPoints}>Solo el doble</button>
              </div>
            </div>
          )}

          {/* Scoring - desktop numpad */}
          {!isActivation && (
            <div className="jn-desktop-numpad-panel">
              <p className="jn-action-title">Cierra — puntos de la tirada</p>
              <div className="jn-input-display">{input || '0'}</div>
              <div className="jn-numpad">
                {[['1','2','3'],['4','5','6'],['7','8','9'],[input.length > 0 ? '←' : 'BUST','0','OK']].map((row, ri) => (
                  <div key={ri} className="jn-numpad-row">
                    {row.map((num) => {
                      const isBack = num === '←';
                      const isBust = num === 'BUST';
                      const isOK = num === 'OK';
                      let cls = 'jn-numpad-btn';
                      if (isBack || isBust) cls += ' danger';
                      if (isOK) cls += ' confirm';
                      return (
                        <button key={num} className={cls} onClick={() => {
                          if (isBack) handleBackspace();
                          else if (isBust) handleBust();
                          else if (isOK) handleScoringSubmit();
                          else handleNumberPress(num);
                        }}>{num}</button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Waiting state when at step 1 */}
          {isActivationPrompt && (
            <div className="jn-desktop-numpad-panel jn-numpad-waiting">
              <div className="jn-waiting-icon">🎯</div>
              <p>Teclado disponible tras activar el doble</p>
            </div>
          )}
        </div>

      </div>{/* end jn-desktop-layout */}

      <div className="jn-playing-sponsors">
        <SponsorsStrip />
      </div>

      {/* Alert */}
      {alert.visible && (
        <div className="jn-modal-overlay" onClick={closeAlert}>
          <div className={`jn-modal ${alert.type}`} onClick={e => e.stopPropagation()}>
            <h3>{alert.title}</h3>
            <p>{alert.message}</p>
            {alert.confirmBust ? (
              <div className="jn-modal-buttons">
                <button className="jn-btn primary" onClick={confirmBustAlert}>✅ Sí, me pasé</button>
                <button className="jn-btn" onClick={closeAlert}>❌ No, corrijo</button>
              </div>
            ) : (
              <button className="jn-btn primary" onClick={closeAlert}>OK</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default JaimeNunezTraining;
